import { Directive, OnChanges, OnDestroy, SimpleChanges, inject } from '@angular/core';
import { IListingSetup, Listing } from '@df/catalog-ui/listing/listing';
import { IProductListingConfig, ProductListing } from '@df/catalog-ui/listing/product-listing';
import { ProductListingFactory } from '@df/catalog-ui/listing/product-listing.factory';
import { RecentlyViewedService } from '@df/catalog-ui/recently-viewed/recently-viewed.service';
import { Model } from '@df/catalog/model';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsContent } from '@icms/core/content';
import { Subscription } from 'rxjs';
import { ModelCarouselService } from '../model-carousel/model-carousel.service';
import { IModelCarouselData, IModelCarouselLoadRequest } from './model-carousel.interface';

@Directive()
// tslint:disable-next-line: directive-class-suffix
export abstract class ModelCarouselComponentDef<T extends Model, D extends IModelCarouselData = IModelCarouselData>
    extends UiComponent
    implements OnDestroy, OnChanges
{
    listing?: Listing | ProductListing;

    oldLoadRequest?: string;

    /**
     * Listing model subscription
     */
    protected listingSubscription?: Subscription;
    protected modelCarouselService = inject(ModelCarouselService);
    protected productListingFactory = inject(ProductListingFactory);

    get models(): T[] {
        return (this.listing?.models as T[]) || [];
    }

    set models(models: T[]) {
        if (this.listing) {
            (this.listing as unknown as Listing<T>).setSourceModels(models);
        }
    }

    abstract type: string;

    @ICmsContent({
        type: 'component'
    })
    component?: D;

    override ngOnInit() {
        super.ngOnInit();
        this.setModels();
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();

        if (this.listing) {
            this.listing.clearSubscriptions();
        }

        this.listingSubscription?.unsubscribe();
    }

    trackById(index: number, item: Model) {
        return item.id;
    }

    override ngOnChanges(changes: SimpleChanges): void {
        if (changes['component']) {
            this.setModels();
        }
    }

    /**
     * Ger models request
     */
    protected getLoadRequest(): IModelCarouselLoadRequest | undefined {
        return this.component;
    }

    protected recentlyViewedService = inject(RecentlyViewedService);

    /**
     * Models updater
     */
    protected setModels() {
        const data = this.getLoadRequest();
        if (data?.isRecentlyViewed) {
            data.isRecentlyViewedItems = this.recentlyViewedService.getItems().map(item => item.id);
        }
        const hash = JSON.stringify(data);
        if (this.oldLoadRequest === hash) {
            super.detectChanges();
            return;
        }
        this.oldLoadRequest = hash;
        const limit = data?.limit;

        if (limit && this.type === 'product') {
            // load 2 times more in case of stock check
            data.limit = (data?.limit || 0) * 2;
        }
        if (data) {
            const state = this.modelCarouselService.getUid(this.type, data);
            const currentState = state;

            this.modelCarouselService.loadModels(this.type, data).then(models => {
                if (state === currentState) {
                    if (this.listing) {
                        this.listing.setSourceModels(models).applyFilters();
                    } else {
                        const options = this.getListingOptions();

                        this.listing =
                            this.type === 'product'
                                ? this.productListingFactory.create(options, [], this.getProductListingSetup()).setSourceModels(models)
                                : new Listing(options, models);
                        this.listing.applyFilters();
                        this.listingSubscription = this.listing.subscribe(() => {
                            this.detectChanges();
                        });
                    }

                    this.listing.setLimit(limit);
                    this.onModelsSet();
                }
            });
        }
    }

    protected getListingOptions(): IProductListingConfig {
        return { type: this.type, stockCheck: true };
    }

    protected getProductListingSetup(): IListingSetup<Product<IProductDataV2>> | undefined {
        return undefined;
    }

    protected onModelsSet(): void {}

    override detectChanges(): void {
        this.setModels();
    }
}
